/**
 * PlayerBrowser — Player lookup with detail view
 */

import { useEffect, useState, useCallback } from 'react';
import { useAdminStore } from '../../../store/adminStore';

export function PlayerBrowser() {
  const {
    players, playersTotalCount, playersOffset, selectedPlayer,
    isLoading, error, loadPlayers, loadPlayerDetail,
  } = useAdminStore();

  const [search, setSearch] = useState('');

  useEffect(() => {
    void loadPlayers(0, '');
  }, [loadPlayers]);

  const handleSearch = useCallback(() => {
    void loadPlayers(0, search);
  }, [loadPlayers, search]);

  const handlePageChange = useCallback((offset: number) => {
    void loadPlayers(offset);
  }, [loadPlayers]);

  const handleSelectPlayer = useCallback((id: string) => {
    void loadPlayerDetail(id);
  }, [loadPlayerDetail]);

  const totalPages = Math.ceil(playersTotalCount / 50);
  const currentPage = Math.floor(playersOffset / 50) + 1;

  return (
    <div>
      <h1 style={styles.title}>👤 玩家查詢</h1>

      {/* Search */}
      <div style={styles.searchBar}>
        <input
          style={{ ...styles.input, flex: 1 }}
          placeholder="搜尋玩家名稱..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
        />
        <button style={styles.actionButton} onClick={handleSearch}>搜尋</button>
        <span style={styles.count}>共 {playersTotalCount} 位玩家</span>
      </div>

      {error && <div style={styles.error}>❌ {error}</div>}

      <div style={styles.content}>
        {/* Player List */}
        <div style={styles.listPanel}>
          <div style={styles.table}>
            <div style={styles.tableHeader}>
              <span style={{ ...styles.cell, flex: 2 }}>玩家名稱</span>
              <span style={styles.cell}>MMR</span>
              <span style={styles.cell}>等級</span>
              <span style={styles.cell}>勝/負</span>
            </div>
            {isLoading && players.length === 0 && (
              <div style={styles.emptyRow}>載入中...</div>
            )}
            {players.map((player) => (
              <div
                key={player.id}
                style={{
                  ...styles.tableRow,
                  backgroundColor: selectedPlayer?.player.id === player.id ? '#3a5a8a' : 'transparent',
                  cursor: 'pointer',
                }}
                onClick={() => { handleSelectPlayer(player.id); }}
              >
                <span style={{ ...styles.cell, flex: 2 }}>{player.username}</span>
                <span style={{ ...styles.cell, color: '#ffcc00' }}>{player.mmr}</span>
                <span style={styles.cell}>{player.level}</span>
                <span style={styles.cell}>
                  <span style={{ color: '#44ff44' }}>{player.wins}</span>
                  {' / '}
                  <span style={{ color: '#ff4444' }}>{player.losses}</span>
                </span>
              </div>
            ))}
            {players.length === 0 && !isLoading && (
              <div style={styles.emptyRow}>沒有找到玩家</div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                style={styles.pageButton}
                disabled={currentPage <= 1}
                onClick={() => { handlePageChange(playersOffset - 50); }}
              >
                ← 上一頁
              </button>
              <span style={styles.pageInfo}>{currentPage} / {totalPages}</span>
              <button
                style={styles.pageButton}
                disabled={currentPage >= totalPages}
                onClick={() => { handlePageChange(playersOffset + 50); }}
              >
                下一頁 →
              </button>
            </div>
          )}
        </div>

        {/* Player Detail */}
        <div style={styles.detailPanel}>
          {selectedPlayer ? (
            <PlayerDetail />
          ) : (
            <div style={styles.placeholder}>
              ← 選擇一位玩家查看詳情
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PlayerDetail() {
  const { selectedPlayer } = useAdminStore();
  if (!selectedPlayer) return null;

  const { player, wallet, collection, total_cards } = selectedPlayer;
  const totalGames = player.wins + player.losses;
  const winRate = totalGames > 0 ? (player.wins / totalGames * 100).toFixed(1) : '0.0';

  return (
    <div>
      <h2 style={styles.detailTitle}>{player.username}</h2>

      {/* Player Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>MMR</span>
          <span style={{ ...styles.statValue, color: '#ffcc00' }}>{player.mmr}</span>
        </div>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>等級</span>
          <span style={styles.statValue}>{player.level}</span>
        </div>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>經驗值</span>
          <span style={styles.statValue}>{player.xp}</span>
        </div>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>勝率</span>
          <span style={styles.statValue}>{winRate}%</span>
        </div>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>勝/負</span>
          <span style={styles.statValue}>
            <span style={{ color: '#44ff44' }}>{player.wins}</span>
            {' / '}
            <span style={{ color: '#ff4444' }}>{player.losses}</span>
          </span>
        </div>
      </div>

      {/* Wallet */}
      <h3 style={styles.sectionTitle}>💰 錢包</h3>
      <div style={styles.infoBox}>
        {wallet ? (
          <span>星靈幣: <strong style={{ color: '#ffcc00' }}>{wallet.coins}</strong></span>
        ) : (
          <span style={{ color: '#888888' }}>尚未建立錢包</span>
        )}
      </div>

      {/* Collection */}
      <h3 style={styles.sectionTitle}>🃏 卡牌收藏 ({total_cards} 張)</h3>
      <div style={styles.collectionGrid}>
        {collection.length > 0 ? (
          collection.map((c) => (
            <div key={c.card_code} style={styles.collectionItem}>
              <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{c.card_code}</span>
              <span style={styles.collectionCount}>×{c.count}</span>
            </div>
          ))
        ) : (
          <span style={{ color: '#888888', fontSize: '13px' }}>暫無收藏</span>
        )}
      </div>

      {/* Timestamps */}
      <div style={styles.timestamps}>
        <span>註冊時間: {new Date(player.created_at).toLocaleString('zh-TW')}</span>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontSize: '24px', margin: '0 0 16px 0' },
  searchBar: { display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' },
  input: {
    padding: '8px 12px', fontSize: '14px', backgroundColor: '#2a2a4a', color: '#ffffff',
    border: '1px solid #3a3a5a', borderRadius: '4px', outline: 'none',
  },
  actionButton: {
    padding: '8px 14px', fontSize: '13px', backgroundColor: '#3a5a8a', color: '#ffffff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
  },
  count: { fontSize: '13px', color: '#888888' },
  error: { color: '#ff4444', padding: '8px', marginBottom: '8px' },
  content: { display: 'flex', gap: '16px', height: 'calc(100vh - 180px)' },
  listPanel: { flex: 1, display: 'flex', flexDirection: 'column' },
  detailPanel: {
    width: '400px', backgroundColor: '#252545', borderRadius: '8px',
    padding: '20px', overflow: 'auto',
  },
  placeholder: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#666666' },
  table: { backgroundColor: '#252545', borderRadius: '8px', overflow: 'hidden', flex: 1 },
  tableHeader: {
    display: 'flex', padding: '10px 12px', backgroundColor: '#1e1e3e',
    fontSize: '12px', fontWeight: 'bold', color: '#888888',
  },
  tableRow: {
    display: 'flex', padding: '10px 12px', borderTop: '1px solid #3a3a5a',
    fontSize: '13px', alignItems: 'center',
  },
  cell: { flex: 1 },
  emptyRow: { padding: '20px', textAlign: 'center', color: '#666666' },
  pagination: { display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '12px', alignItems: 'center' },
  pageButton: {
    padding: '6px 12px', fontSize: '12px', backgroundColor: '#3a5a8a', color: '#ffffff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
  },
  pageInfo: { fontSize: '13px', color: '#888888' },
  detailTitle: { fontSize: '20px', margin: '0 0 16px 0' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' },
  statItem: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '12px', backgroundColor: '#1e1e3e', borderRadius: '6px',
  },
  statLabel: { fontSize: '11px', color: '#888888', marginBottom: '4px' },
  statValue: { fontSize: '18px', fontWeight: 'bold' },
  sectionTitle: { fontSize: '14px', margin: '16px 0 8px 0', color: '#cccccc' },
  infoBox: { padding: '12px', backgroundColor: '#1e1e3e', borderRadius: '6px', fontSize: '14px' },
  collectionGrid: {
    display: 'flex', flexWrap: 'wrap', gap: '4px',
    maxHeight: '200px', overflow: 'auto', padding: '8px', backgroundColor: '#1e1e3e', borderRadius: '6px',
  },
  collectionItem: {
    display: 'flex', gap: '4px', alignItems: 'center',
    padding: '4px 8px', backgroundColor: '#2a2a4a', borderRadius: '4px', fontSize: '12px',
  },
  collectionCount: { color: '#ffcc00', fontWeight: 'bold' },
  timestamps: { marginTop: '20px', fontSize: '11px', color: '#666666' },
};
